// BEGIN FILE: src/utils/analytics.ts
import { supabase } from './supabaseClient';
import { getCachedUser } from './cachedUser';

let currentSessionId: string | null = null;
let currentTabActivity: string | null = null;
let tabStartTime: number | null = null;
let cachedUserId: string | null = null; // Cache user ID to avoid repeated getUser() calls
let lastTabChangeTime: number = 0; // Throttle tab tracking

/**
 * Start a new user session when they log in
 */
export async function startUserSession(): Promise<string | null> {
  try {
    const user = await getCachedUser();
    if (!user) return null;

    cachedUserId = user.id; // Cache user ID
    const userAgent = navigator.userAgent;

    const { data, error } = await supabase
      .from('user_sessions')
      .insert({
        user_id: user.id,
        user_agent: userAgent,
        started_at: new Date().toISOString(),
      })
      .select('id')
      .single();

    if (error) {
      console.error('Failed to start session:', error);
      return null;
    }

    currentSessionId = data.id;
    return data.id;
  } catch (err) {
    console.error('Session start error:', err);
    return null;
  }
}

/**
 * End the current user session when they log out or close the app
 */
export async function endUserSession(): Promise<void> {
  if (!currentSessionId) return;

  try {
    const user = await getCachedUser();
    if (!user) return;

    // End any active tab tracking first
    await endTabActivity();

    // Get session start time to calculate duration
    const { data: session } = await supabase
      .from('user_sessions')
      .select('started_at')
      .eq('id', currentSessionId)
      .single();

    if (session) {
      const startTime = new Date(session.started_at).getTime();
      const endTime = Date.now();
      const durationSeconds = Math.floor((endTime - startTime) / 1000);

      await supabase
        .from('user_sessions')
        .update({
          ended_at: new Date().toISOString(),
          duration_seconds: durationSeconds,
        })
        .eq('id', currentSessionId);
    }

    currentSessionId = null;
  } catch (err) {
    console.error('Session end error:', err);
  }
}

/**
 * Track when user switches to a new tab (with cached user ID + throttling)
 */
export async function startTabActivity(tabName: string): Promise<void> {
  try {
    // Throttle: Only track if more than 10 seconds passed since last change
    const now = Date.now();
    if (now - lastTabChangeTime < 10000) {
      return; // Skip tracking if tab changed too recently
    }
    lastTabChangeTime = now;

    // End previous tab activity if any
    await endTabActivity();

    // Use cached user ID instead of fetching
    if (!cachedUserId) {
      const user = await getCachedUser();
      if (!user) return;
      cachedUserId = user.id;
    }

    // Ensure we have a session
    if (!currentSessionId) {
      currentSessionId = await startUserSession();
      if (!currentSessionId) return;
    }

    const { data, error } = await supabase
      .from('user_tab_activity')
      .insert({
        user_id: cachedUserId,
        session_id: currentSessionId,
        tab_name: tabName,
        started_at: new Date().toISOString(),
      })
      .select('id')
      .single();

    if (error) {
      console.error('Failed to start tab activity:', error);
      return;
    }

    currentTabActivity = data.id;
    tabStartTime = Date.now();
  } catch (err) {
    console.error('Tab activity start error:', err);
  }
}

/**
 * End tracking for the current tab
 */
export async function endTabActivity(): Promise<void> {
  if (!currentTabActivity || !tabStartTime) return;

  try {
    const endTime = Date.now();
    const durationSeconds = Math.floor((endTime - tabStartTime) / 1000);

    await supabase
      .from('user_tab_activity')
      .update({
        ended_at: new Date().toISOString(),
        duration_seconds: durationSeconds,
      })
      .eq('id', currentTabActivity);

    currentTabActivity = null;
    tabStartTime = null;
  } catch (err) {
    console.error('Tab activity end error:', err);
  }
}

/**
 * Update last active timestamp (call periodically while user is active)
 */
export async function updateActivityHeartbeat(): Promise<void> {
  try {
    // Use cached user ID to avoid repeated getUser() calls
    if (!cachedUserId) {
      const user = await getCachedUser();
      if (!user) return;
      cachedUserId = user.id;
    }

    // Upsert activity summary with updated last_active_at
    await supabase
      .from('user_activity_summary')
      .upsert({
        user_id: cachedUserId,
        last_active_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'user_id',
      });
  } catch (err) {
    console.error('Heartbeat error:', err);
  }
}

/**
 * Initialize analytics tracking
 * Call this when app loads and user is authenticated
 * Returns cleanup function to clear interval and remove event listeners
 */
export function initializeAnalytics(): () => void {
  // Start session on load
  startUserSession();

  // Send initial heartbeat immediately
  updateActivityHeartbeat();

  // Then send heartbeat every 12 hours (twice per day)
  const heartbeatInterval = setInterval(() => {
    updateActivityHeartbeat();
  }, 12 * 60 * 60 * 1000); // 12 hours

  // Define cleanup handlers
  const beforeUnloadHandler = () => {
    clearInterval(heartbeatInterval);
    endUserSession();
  };

  const visibilityChangeHandler = () => {
    if (document.hidden) {
      endTabActivity();
    }
  };

  // Add event listeners
  window.addEventListener('beforeunload', beforeUnloadHandler);
  document.addEventListener('visibilitychange', visibilityChangeHandler);

  // Return cleanup function
  return () => {
    clearInterval(heartbeatInterval);
    window.removeEventListener('beforeunload', beforeUnloadHandler);
    document.removeEventListener('visibilitychange', visibilityChangeHandler);
  };
}

/**
 * Get user analytics data (for admin view)
 */
const STATUS_PRIORITY: Record<string, number> = {
  active: 0,
  recent: 1,
  today: 2,
  this_week: 3,
  inactive: 4,
};

export async function getUserAnalytics() {
  try {
    const { data, error } = await supabase
      .from('user_analytics_view')
      .select('*');

    if (error) {
      console.error('Error fetching analytics:', error);
      throw new Error(`Database error: ${error.message} (Code: ${error.code})`);
    }

    const rows = data || [];
    // Sort: active users first by status priority, then by last_active_at DESC
    rows.sort((a: any, b: any) => {
      const pa = STATUS_PRIORITY[a.activity_status] ?? 5;
      const pb = STATUS_PRIORITY[b.activity_status] ?? 5;
      if (pa !== pb) return pa - pb;
      const ta = a.last_active_at ? new Date(a.last_active_at).getTime() : 0;
      const tb = b.last_active_at ? new Date(b.last_active_at).getTime() : 0;
      return tb - ta;
    });

    return rows;
  } catch (err: any) {
    console.error('Failed to fetch analytics:', err);
    throw err;
  }
}

/**
 * Format duration in seconds to human-readable format
 */
export function formatDuration(seconds: number | null): string {
  if (!seconds || seconds < 0) return '0m';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  } else {
    return `${secs}s`;
  }
}

/**
 * Format status badge color
 */
export function getStatusColor(status: string): string {
  switch (status) {
    case 'active': return '#10b981'; // green
    case 'recent': return '#3b82f6'; // blue
    case 'today': return '#8b5cf6'; // purple
    case 'this_week': return '#f59e0b'; // amber
    case 'inactive': return '#6b7280'; // gray
    default: return '#6b7280';
  }
}

/**
 * Format status label
 */
export function getStatusLabel(status: string): string {
  switch (status) {
    case 'active': return 'Active Now';
    case 'recent': return 'Recent (<1h)';
    case 'today': return 'Today';
    case 'this_week': return 'This Week';
    case 'inactive': return 'Inactive';
    default: return 'Unknown';
  }
}

// END FILE: src/utils/analytics.ts
